import { useCallback, useEffect, useMemo, useState } from "react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { useAuth } from "@/contexts/AuthContext";
import { listManufacturingPlants, type MfgPlant } from "@/lib/manufacturingClient";
import { listManufacturingPurchaseOrders } from "@/lib/manufacturing/purchaseOrderClient";
import {
  onOrderQtyByMaterialId,
  summarizePurchasingSpend,
} from "@/lib/manufacturing/purchaseOrderSpend";
import type { MfgPurchaseOrder } from "@/lib/manufacturing/purchaseOrderTypes";
import {
  canSeePlantRequestStatusFromHasRole,
  canSeePurchaserRequestAlertFromHasRole,
  isPlantOnlyPurchasingSeatFromHasRole,
} from "@/lib/manufacturing/plantMaterialRequestAccess";
import { GeneratePurchaseOrderBody } from "./GeneratePurchaseOrderBody";
import { ManufacturingGateBanner } from "./ManufacturingGateBanner";
import { ManufacturingInventoryReceiptsBody } from "./ManufacturingInventoryReceiptsBody";
import { ManufacturingVendorsPanel } from "./ManufacturingVendorsPanel";
import { ManufacturingPurchasingSpendStrip } from "./ManufacturingPurchasingSpendStrip";
import { ManufacturingReceiptsAttentionBanner } from "./ManufacturingReceiptsAttentionBanner";
import { ManufacturingTabbedSection } from "./ManufacturingTabbedSection";
import {
  purchasingTabsForSeat,
  type PurchasingTab,
} from "./manufacturingPurchasingTabs";
import { PurchaseOrderSettingsBody } from "./PurchaseOrderSettingsBody";
import { ManufacturingInventoryMatchPanel } from "./ManufacturingInventoryMatchPanel";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { useManufacturingMaterials } from "./useManufacturingMaterials";
import { useManufacturingReceiptsAttention } from "./useManufacturingReceiptsAttention";
import {
  PlantMaterialRequestPurchaserBanner,
  PlantMaterialRequestStatusStrip,
} from "./PlantMaterialRequestAlerts";
import { NegativeOnHandBanner } from "./NegativeOnHandBanner";
import { PlantMaterialRequestCompose } from "./PlantMaterialRequestCompose";
import { PlantMaterialRequestInbox } from "./PlantMaterialRequestInbox";

export function ManufacturingPurchasingPanel() {
  const gate = useManufacturingEntitlementGate();
  const auth = useAuth();
  const hasRole = (...r: string[]) => (auth?.status === "ready" ? auth.hasRole(...r) : false);
  const plantOnly = isPlantOnlyPurchasingSeatFromHasRole(hasRole);
  const showPurchaserAlert = canSeePurchaserRequestAlertFromHasRole(hasRole);
  const showStatusStrip = canSeePlantRequestStatusFromHasRole(hasRole) || plantOnly;
  const materials = useManufacturingMaterials({ enabled: gate.enabled });
  const receiptsAttention = useManufacturingReceiptsAttention({ enabled: gate.enabled });
  const tabs = purchasingTabsForSeat(plantOnly);
  const [tab, setTab] = useState<PurchasingTab>("purchaseOrders");
  const [orders, setOrders] = useState<MfgPurchaseOrder[]>([]);
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [reviewRequests, setReviewRequests] = useState(false);
  const [focusRequestId, setFocusRequestId] = useState<string | null>(null);
  const [focusPoId, setFocusPoId] = useState<string | null>(null);

  const reloadOrders = useCallback(async () => {
    if (!gate.enabled) {
      setOrders([]);
      return;
    }
    const res = await listManufacturingPurchaseOrders();
    if (res.ok) setOrders(Array.isArray(res.data.orders) ? res.data.orders : []);
  }, [gate.enabled]);

  useEffect(() => {
    void reloadOrders();
  }, [reloadOrders]);

  useEffect(() => {
    if (!gate.enabled || !plantOnly) return;
    void listManufacturingPlants().then((p) => {
      if (p.ok) setPlants(Array.isArray(p.data.plants) ? p.data.plants : []);
    });
  }, [gate.enabled, plantOnly]);

  const spend = useMemo(() => summarizePurchasingSpend(orders), [orders]);
  const onOrderCount = useMemo(() => onOrderQtyByMaterialId(orders).size, [orders]);

  return (
    <div
      className="w-full space-y-4 text-left"
      {...(gate.enabled ? { [FIREFLY_TARGET_ATTR]: "mfg-purchasing-surface" } : {})}
    >
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">Purchasing Assistant</h2>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          {plantOnly
            ? "Request materials that are running low. Purchasing will acknowledge and order them."
            : "Purchase orders, vendor invoices, and on-the-ground delivery confirmation. Issued qty stays on-order until invoice + delivery accept it into inventory."}
        </p>
      </div>
      <ManufacturingGateBanner gate={gate} />
      {gate.enabled ? (
        <>
          <NegativeOnHandBanner />
          {showPurchaserAlert ? (
            <PlantMaterialRequestPurchaserBanner
              orders={orders}
              onReview={() => {
                setTab("purchaseOrders");
                setReviewRequests(true);
              }}
            />
          ) : null}
          {showStatusStrip ? <PlantMaterialRequestStatusStrip orders={orders} /> : null}
          {plantOnly ? null : (
            <PlantMaterialRequestInbox
              orders={orders}
              onOpen={(id) => {
                setTab("purchaseOrders");
                setReviewRequests(true);
                setFocusPoId(null);
                setFocusRequestId(id);
              }}
            />
          )}
          {plantOnly ? null : (
            <ManufacturingPurchasingSpendStrip summary={spend} onOrderLineCount={onOrderCount} />
          )}
          <ManufacturingReceiptsAttentionBanner
            count={receiptsAttention.count}
            items={receiptsAttention.items}
            onSwitchToReceipts={() => setTab("receipts")}
          />
          <ManufacturingTabbedSection<PurchasingTab>
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
          >
            {tab === "purchaseOrders" ? (
              plantOnly ? (
                <div className="mt-3 rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
                  <PlantMaterialRequestCompose
                    enabled={gate.enabled}
                    plants={plants}
                    materials={materials.materials}
                    onSubmitted={() => void reloadOrders()}
                  />
                </div>
              ) : (
                <GeneratePurchaseOrderBody
                  enabled={gate.enabled}
                  materials={materials.materials}
                  onMaterialsRefresh={materials.refresh}
                  onOrdersChanged={setOrders}
                  reviewRequests={reviewRequests}
                  focusRequestId={focusPoId ?? focusRequestId}
                />
              )
            ) : tab === "vendors" ? (
              <div className="mt-3">
                <ManufacturingVendorsPanel />
              </div>
            ) : tab === "receipts" ? (
              <ManufacturingInventoryReceiptsBody enabled={gate.enabled} />
            ) : tab === "outlook" ? (
              <div className="mt-3">
                <ManufacturingInventoryMatchPanel
                  enabled={gate.enabled}
                  onDraftCreated={(poId) => {
                    setFocusRequestId(null);
                    setFocusPoId(poId);
                    setTab("purchaseOrders");
                  }}
                />
              </div>
            ) : (
              <PurchaseOrderSettingsBody enabled={gate.enabled} />
            )}
          </ManufacturingTabbedSection>
        </>
      ) : null}
    </div>
  );
}
